import translate from '@translate';
import React, { FC } from 'react';
import Grid, { GridItem } from 'src/components/Grid';
import TextField from 'src/components/TextField';
import MultiEmailField from 'src/components/MultiEmailField';
import { ProviderType } from 'src/types/provider';
import Switch from 'src/components/Switch';
import { ConstantsCodeType } from 'src/types/constantsCodes';
import Select from 'src/components/Select';
import { FormErrorsType, OnChangeType } from '../../types/global';
import { EditKeysType } from './constants';
import getCountryOptions from 'src/utils/countryOptions';

export type OnChangeMultipleType = (updates: Partial<ProviderType>) => void;

export interface DetailPropsType {
    data: ProviderType | null;
    onChange: OnChangeType<EditKeysType>;
    onChangeMultiple: OnChangeMultipleType;
    editData: ProviderType;
    editErrors: FormErrorsType;
    newProvider: boolean;
    disableEdit: boolean;
    providerSystemCodes: ConstantsCodeType[];
    providersCodes: ConstantsCodeType[];
    clientTypeCodes: ConstantsCodeType[];
    businessFieldCodes: ConstantsCodeType[];
    salesRepresentativeCodes: ConstantsCodeType[];
    keyAccountManagerCodes: ConstantsCodeType[];
    permissionOnboardingEdit: boolean;
}

const Detail: FC<DetailPropsType> = (props) => {
    return (<>
            <Grid>
                <GridItem cols={3}>
                    <TextField
                        value={props.editData.BUSINESS_NAME}
                        error={props.editErrors.BUSINESS_NAME}
                        onChange={(value, error) => props.onChange('BUSINESS_NAME', value, error)}
                        name="BUSINESS_NAME"
                        label={translate('providerDetail.businessName')}
                        fullWidth
                        required
                        disabled={props.disableEdit}
                    />
                </GridItem>
                <GridItem cols={3}>
                    <TextField
                        value={props.editData.COMPANY_NAME}
                        error={props.editErrors.COMPANY_NAME}
                        onChange={(value, error) => props.onChange('COMPANY_NAME', value, error)}
                        name="COMPANY_NAME"
                        label={translate('providerDetail.eshopName')}
                        fullWidth
                        required
                        disabled={props.disableEdit}
                    />
                </GridItem>
                <GridItem cols={3}>
                    <TextField
                        value={props.editData.WEBSITE}
                        error={props.editErrors.WEBSITE}
                        onChange={(value, error) => props.onChange('WEBSITE', value, error)}
                        name="WEBSITE"
                        label={translate('providerDetail.providerWebsite')}
                        fullWidth
                        required
                        disabled={props.disableEdit}
                    />
                </GridItem>
                <GridItem cols={3}>
                    <Select
                        options={props.providerSystemCodes}
                        value={props.editData.SYSTEM}
                        error={props.editErrors.SYSTEM}
                        onChange={(value, error) => props.onChange('SYSTEM', value, error)}
                        name="SYSTEM"
                        label={translate('providerDetail.system')}
                        fullWidth
                        disabled={props.disableEdit}
                        required
                    />
                </GridItem>
                <GridItem cols={3}>
                    <Select
                        options={props.clientTypeCodes || []}
                        value={props.editData.CLIENT_TYPE}
                        error={props.editErrors.CLIENT_TYPE}
                        onChange={(value, error) => props.onChange('CLIENT_TYPE', value, error)}
                        name="CLIENT_TYPE"
                        label={translate('providerDetail.clientType')}
                        fullWidth
                        disabled={props.disableEdit}
                        required
                    />
                </GridItem>
                <GridItem cols={3}>
                    <Select
                        options={props.businessFieldCodes || []}
                        value={props.editData.BUSINESS_FIELD}
                        error={props.editErrors.BUSINESS_FIELD}
                        onChange={(value, error) => props.onChange('BUSINESS_FIELD', value, error)}
                        name="BUSINESS_FIELD"
                        label={translate('providerDetail.businessField')}
                        fullWidth
                        disabled={props.disableEdit}
                        required
                    />
                </GridItem>
                <GridItem cols={3}>
                    <Select
                        options={props.salesRepresentativeCodes}
                        value={props.editData.SALES_REPRESENTATIVE}
                        error={props.editErrors.SALES_REPRESENTATIVE}
                        onChange={(value, error) => props.onChange('SALES_REPRESENTATIVE', value, error)}
                        name="SALES_REPRESENTATIVE"
                        label={translate('providerDetail.salesRepresentative')}
                        fullWidth
                        disabled={props.disableEdit}
                        required
                    />
                </GridItem>
                <GridItem cols={3}>
                    <Select
                        options={props.keyAccountManagerCodes}
                        value={props.editData.KEY_ACCOUNT_MANAGER}
                        error={props.editErrors.KEY_ACCOUNT_MANAGER}
                        onChange={(value, error) => props.onChange('KEY_ACCOUNT_MANAGER', value, error)}
                        name="KEY_ACCOUNT_MANAGER"
                        label={translate('providerDetail.keyAccountManager')}
                        fullWidth
                        disabled={props.disableEdit}
                        required
                    />
                </GridItem>
                <GridItem cols={3}>
                    <TextField
                        value={props.editData.PROVIDER_STREET}
                        error={props.editErrors.PROVIDER_STREET}
                        onChange={(value, error) => props.onChange('PROVIDER_STREET', value || '', error)}
                        name="PROVIDER_STREET"
                        label={translate('providerDetail.street')}
                        fullWidth
                        disabled={props.disableEdit}
                        required
                    />
                </GridItem>
                <GridItem cols={3}>
                    <TextField
                        value={props.editData.PROVIDER_CITY}
                        error={props.editErrors.PROVIDER_CITY}
                        onChange={(value, error) => props.onChange('PROVIDER_CITY', value || '', error)}
                        name="PROVIDER_CITY"
                        label={translate('providerDetail.city')}
                        fullWidth
                        disabled={props.disableEdit}
                        required
                    />
                </GridItem>
                <GridItem cols={3}>
                    <TextField
                        value={props.editData.PROVIDER_POST_CODE}
                        error={props.editErrors.PROVIDER_POST_CODE}
                        onChange={(value, error) => props.onChange('PROVIDER_POST_CODE', value || '', error)}
                        name="PROVIDER_POST_CODE"
                        label={translate('providerDetail.postCode')}
                        fullWidth
                        disabled={props.disableEdit}
                        required
                        maxLength={9}
                    />
                </GridItem>
                <GridItem cols={3}>
                    <Select
                        options={getCountryOptions()}
                        value={props.editData.PROVIDER_STATE}
                        error={props.editErrors.PROVIDER_STATE}
                        onChange={(value, error) => props.onChange('PROVIDER_STATE', value, error)}
                        name="PROVIDER_STATE"
                        label={translate('providerDetail.state')}
                        fullWidth
                        disabled={props.disableEdit}
                        required
                    />
                </GridItem>
                <GridItem cols={3}>
                    <TextField
                        value={props.editData.PROVIDER_BILLING_STREET}
                        error={props.editErrors.PROVIDER_BILLING_STREET}
                        onChange={(value, error) => props.onChange('PROVIDER_BILLING_STREET', value || '', error)}
                        name="PROVIDER_BILLING_STREET"
                        label={translate('providerDetail.billingStreet')}
                        fullWidth
                        disabled={props.disableEdit}
                        required
                    />
                </GridItem>
                <GridItem cols={3}>
                    <TextField
                        value={props.editData.PROVIDER_BILLING_CITY}
                        error={props.editErrors.PROVIDER_BILLING_CITY}
                        onChange={(value, error) => props.onChange('PROVIDER_BILLING_CITY', value || '', error)}
                        name="PROVIDER_BILLING_CITY"
                        label={translate('providerDetail.billingCity')}
                        fullWidth
                        disabled={props.disableEdit}
                        required
                    />
                </GridItem>
                <GridItem cols={3}>
                    <TextField
                        value={props.editData.BILLING_POST_CODE}
                        error={props.editErrors.BILLING_POST_CODE}
                        onChange={(value, error) => props.onChange('BILLING_POST_CODE', value || '', error)}
                        name="BILLING_POST_CODE"
                        label={translate('providerDetail.billingPostCode')}
                        fullWidth
                        disabled={props.disableEdit}
                        required
                        maxLength={9}
                    />
                </GridItem>
                <GridItem cols={3}>
                    <Select
                        options={getCountryOptions()}
                        value={props.editData.PROVIDER_BILLING_STATE}
                        error={props.editErrors.PROVIDER_BILLING_STATE}
                        onChange={(value, error) => props.onChange('PROVIDER_BILLING_STATE', value, error)}
                        name="PROVIDER_BILLING_STATE"
                        label={translate('providerDetail.billingState')}
                        fullWidth
                        disabled={props.disableEdit}
                        required
                    />
                </GridItem>
                <GridItem cols={3}>
                    <TextField
                        value={props.editData.PROVIDER_COMPANY_ID}
                        error={props.editErrors.PROVIDER_COMPANY_ID}
                        onChange={(value, error) => props.onChange('PROVIDER_COMPANY_ID', value || null, error)}
                        name="PROVIDER_COMPANY_ID"
                        label={translate('providerDetail.companyId')}
                        fullWidth
                        disabled={props.disableEdit}
                        required
                        maxLength={10}
                        inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                    />
                </GridItem>
                <GridItem cols={3}>
                    <TextField
                        value={props.editData.PROVIDER_TAX_ID}
                        error={props.editErrors.PROVIDER_TAX_ID}
                        onChange={(value, error) => props.onChange('PROVIDER_TAX_ID', value || '', error)}
                        name="PROVIDER_TAX_ID"
                        label={translate('providerDetail.taxId')}
                        fullWidth
                        disabled={props.disableEdit}
                        maxLength={12}
                        required
                    />
                </GridItem>
                <GridItem cols={3}>
                    <TextField
                        value={props.editData.EORI}
                        error={props.editErrors.EORI}
                        onChange={(value, error) => props.onChange('EORI', value || '', error)}
                        name="EORI"
                        label={translate('providerDetail.EORI')}
                        fullWidth
                        disabled={props.disableEdit}
                    />
                </GridItem>
                <GridItem cols={3}>
                    <TextField
                        value={props.editData.GLN}
                        error={props.editErrors.GLN}
                        onChange={(value, error) => props.onChange('GLN', value || '', error)}
                        name="GLN"
                        label={translate('providerDetail.GLN')}
                        fullWidth
                        disabled={props.disableEdit}
                    />
                </GridItem>
                <GridItem cols={3}>
                    <TextField
                        value={props.editData.CONTACT_PERSON}
                        error={props.editErrors.CONTACT_PERSON}
                        onChange={(value, error) => props.onChange('CONTACT_PERSON', value || '', error)}
                        name="CONTACT_PERSON"
                        label={translate('providerDetail.contactPerson')}
                        fullWidth
                        disabled={props.disableEdit}
                        required
                    />
                </GridItem>
                <GridItem cols={3}>
                    <TextField
                        value={props.editData.PROVIDER_EMAIL}
                        error={props.editErrors.PROVIDER_EMAIL}
                        onChange={(value, error) => props.onChange('PROVIDER_EMAIL', value, error)}
                        name="PROVIDER_EMAIL"
                        label={translate('providerDetail.email')}
                        fullWidth
                        required
                        disabled={props.disableEdit}
                        type="email"
                    />
                </GridItem>
                <GridItem cols={3}>
                    <TextField
                        value={props.editData.PROVIDER_COMPANY_PHONE}
                        error={props.editErrors.PROVIDER_COMPANY_PHONE}
                        onChange={(value, error) => props.onChange('PROVIDER_COMPANY_PHONE', value || '', error)}
                        name="PROVIDER_COMPANY_PHONE"
                        label={translate('providerDetail.phone')}
                        fullWidth
                        disabled={props.disableEdit}
                        required
                        maxLength={14}
                    />
                </GridItem>
                <GridItem cols={3}>
                    <MultiEmailField
                        value={props.editData.BILLING_EMAIL}
                        error={props.editErrors.BILLING_EMAIL}
                        onChange={(value, error) => props.onChange('BILLING_EMAIL', value || '', error)}
                        name="BILLING_EMAIL"
                        label={translate('providerDetail.billingEmail')}
                        fullWidth
                        disabled={props.disableEdit}
                        required
                    />
                </GridItem>
            </Grid>
        </>
    );
};

export default Detail;
